/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React from 'react';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import Context, { type ExperienceContext } from '../common/context/index.tsx';
import type { ProviderProps, SubProviderProps } from '../common/types.tsx';
import { ExperienceTracker } from '../experience-tracker-consumer/tracker-base/index.tsx';
import ExperienceTrackingProvider from './view.tsx';

const additionalAttributesDefaultValue = {};

export const ViewExperienceTrackingProvider: ({
	isExperienceReady,
	experience,
	experienceId,
	analyticsSource,
	application,
	edition,
	additionalAttributes,
	children,
}: ProviderProps) => React.JSX.Element = ({
	isExperienceReady = true,
	experience,
	experienceId = null,
	analyticsSource,
	application,
	edition,
	additionalAttributes = additionalAttributesDefaultValue,
	children,
}: ProviderProps): React.JSX.Element =>
	__SERVER__ ? (
		<>{children}</>
	) : (
		<ExperienceTrackingProvider
			isExperienceReady={isExperienceReady}
			experience={experience}
			experienceId={experienceId}
			analyticsSource={analyticsSource}
			application={application}
			edition={edition}
			additionalAttributes={additionalAttributes}
		>
			{children}
		</ExperienceTrackingProvider>
	);

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports, jira/export/restrict-types-re-export
export type { ProviderProps };

export default ViewExperienceTrackingProvider;

export const SubExperienceTrackingProvider: ({
	children,
	location,
	parentProviders,
}: SubProviderProps) => React.JSX.Element = ({
	children,
	location,
	parentProviders = null,
}: SubProviderProps): React.JSX.Element =>
	__SERVER__ ? (
		<>{children}</>
	) : (
		// DO NOT use `useContext` or `static contextType = ...` to consume this context
		// as the tree walker used in SSR does not support hooks
		// and SSR does not support `static contextType` setting
		<Context.Consumer>
			{(context: ExperienceContext | null) => {
				if (context == null) {
					log.safeErrorWithoutCustomerData(
						'experience-sub-provider.missing-provider',
						'Experience sub-provider rendered without a parent provider',
					);
					return children;
				}
				const { experienceId } = context;

				return (
					<ExperienceTracker location={location} parentProviders={parentProviders}>
						{(trackerCallbacks) => (
							<ExperienceTrackingProvider
								parentTrackerCallbacks={trackerCallbacks}
								experience={location}
								experienceId={experienceId}
								analyticsSource={null}
								application={null}
								edition={null}
								isExperienceReady
							>
								{children}
							</ExperienceTrackingProvider>
						)}
					</ExperienceTracker>
				);
			}}
		</Context.Consumer>
	);
